import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CART_ANALYTICS_ACTIONS } from './cart-analytics.actions';
import { Store } from '@ngrx/store';
import { PanAppState, Tracking } from '@panamax/app-state';
import { AddToOrderService } from '@order/tracking/shared/services/add-to-order.service';
import {
  appStateForProductsSelector,
  productSelector,
} from '@shared/selectors/product.selectors';
import { combineLatest, of } from 'rxjs';
import { CART_ACTIONS, OrderStatus, partnerSelectors } from '@usf/ngrx-order';
import { SharedTrackingService } from '@shared/services/analytics/shared-tracking.service';
import { OrderSubmittedAnalyticsMessages } from '@order/pages/order-submitted/tracking/analytics/order-submitted-analytics.constants';
import { OrderSubmittedAnalyticsService } from 'src/app/order/pages/order-submitted/tracking/analytics/order-submitted-analytics.service';
import { OrderAnalyticsService } from '../order-analytics.service';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { OrderAnalyticsMessages } from '../order-analytics.constants';
import { genericAnalyticsTransformer } from '@shared/tracking/analytics/generic-analytics-transformer';
import { cancelSubmittedCartAnalytics } from './cancel-submitted-cart-analytics.factory';
import { cancelInProgressCartAnalytics } from './cancel-in-progress-cart-analytics.factory';
import { OrderService } from '@order/services/order.service';
import {
  OrderSubmittedTracingMessages,
  OrderTracingMessagesTest,
} from '@order/pages/order-submitted/tracking/tracing/order-submitted-tracing.constants';
import { RemoveFromOrderService } from '@order/tracking/shared/services/remove-from-order.service';
import { selectProductPricingState } from '@usf/ngrx-pricing';
import { selectedCustomer } from '@app/ngrx-customer/store';

@Injectable({
  providedIn: 'root',
})
export class CartAnalyticsEffects {
  addToOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ANALYTICS_ACTIONS.createAddToOrderTracking),
      filter(action =>
        AddToOrderService.isQuantityIncreasing(action.updateOrderItemSequence),
      ),
      mergeMap(action => {
        const orderItems = [action.updateOrderItemSequence];
        return combineLatest([
          of(action),
          this.orderService.getProductsForTracing$(orderItems),
          this.panAppState.customer$,
        ]).pipe(take(1));
      }),
      map(([action, productMap, customer]) => {
        return AddToOrderService.createAnalytics(
          action,
          {
            ...action,
            orderHeader: {
              ...action.updateOrderHeader,
            },
          },
          customer,
          productMap,
        );
      }),
      switchMap(tracking => {
        return of(
          CART_ANALYTICS_ACTIONS.trackAddToOrder({
            tracking,
          }),
        );
      }),
    ),
  );

  removeFromOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ANALYTICS_ACTIONS.createRemoveFromOrderTracking),
      mergeMap(action => {
        const orderItems = [action.updateOrderItemSequence];
        return combineLatest([
          of(action),
          this.orderService.getProductsForTracing$(orderItems),
          this.panAppState.customer$,
          this.store.select(selectProductPricingState),
        ]).pipe(take(1));
      }),
      map(([action, productMap, customer, pricingState]) => {
        return RemoveFromOrderService.createAnalytics(
          action,
          {
            ...action,
            orderHeader: {
              ...action.updateOrderHeader,
            },
          },
          customer,
          productMap,
          pricingState.entities,
        );
      }),
      switchMap(tracking => {
        return of(
          CART_ANALYTICS_ACTIONS.trackRemoveFromOrder({
            tracking,
          }),
        );
      }),
    ),
  );

  updateUqeQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ANALYTICS_ACTIONS.createUpdateUqeTracking),
      mergeMap(action => {
        const orderItems = [action.updateOrderItemSequence];
        return combineLatest([
          of(action),
          this.orderService.getProductsForTracing$(orderItems),
          this.panAppState.customer$,
        ]).pipe(take(1));
      }),
      map(([action, productMap, customer]) => {
        const uqeTracking = AddToOrderService.createAnalytics(
          action,
          {
            ...action,
            orderHeader: {
              ...action.updateOrderHeader,
            },
          },
          customer,
          productMap,
        );

        //if UQE item is increasing in quantity, we will also dispatch trackAddToOrder with updated tracking data
        const isQuantityIncreasing = AddToOrderService.isQuantityIncreasing(
          action.updateOrderItemSequence,
        );

        let addToOrderTracking: Tracking;
        if (isQuantityIncreasing) {
          addToOrderTracking = AddToOrderService.createAnalytics(
            action,
            {
              ...action,
              orderHeader: {
                ...action.updateOrderHeader,
              },
            },
            customer,
            productMap,
          );
          addToOrderTracking.analytics.data.event =
            OrderAnalyticsMessages.addToOrder;
          const productAnalytics =
            addToOrderTracking.analytics.data.products[0];
          addToOrderTracking.analytics.data.products[0] = {
            ...productAnalytics,
            addMethod: OrderAnalyticsMessages.cart,
          };
        }
        return { uqeTracking, addToOrderTracking, isQuantityIncreasing };
      }),
      switchMap(tracking => {
        return tracking.isQuantityIncreasing
          ? of(
              CART_ANALYTICS_ACTIONS.trackUpdateUqeQuantity({
                tracking: tracking.uqeTracking,
              }),
              CART_ANALYTICS_ACTIONS.trackAddToOrder({
                tracking: tracking.addToOrderTracking,
              }),
            )
          : of(
              CART_ANALYTICS_ACTIONS.trackUpdateUqeQuantity({
                tracking: tracking.uqeTracking,
              }),
            );
      }),
    ),
  );

  trackSubmitOrderApiSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CART_ACTIONS.submitCartSuccess),
        mergeMap(resp =>
          combineLatest([
            of(resp),
            this.orderService.loadOrderViewModel$().pipe(take(1)),
            this.store.select(partnerSelectors.selectPartner).pipe(take(1)),
            this.store.select(appStateForProductsSelector).pipe(take(1)),
            this.store.select(selectedCustomer).pipe(take(1)),
          ]),
        ),
        map(
          ([
            resp,
            order,
            partnerState,
            appStateForProduct,
            selectedCustomer,
          ]) => {
            const productMap =
              this.orderService.updateAvailableToPromiseOnOrderItems(
                order.productsMap,
                appStateForProduct,
                selectedCustomer,
                partnerState,
                resp.order?.orderHeader,
                resp.order?.orderItems,
              );

            const products = SharedTrackingService.analyticsProductData(
              resp.order?.orderItems,
              productMap,
              resp.order?.orderHeader.divisionNumber,
              undefined,
              undefined,
              true,
            );
            const tracingProducts = SharedTrackingService.tracingProductData(
              resp.order?.orderItems,
              productMap,
            );
            const orderId = resp?.order?.orderHeader?.orderId;
            const origOrderId = resp?.order?.orderHeader?.origOrderId;
            const filteredOrderId = origOrderId ? origOrderId : orderId;
            const analyticsOrderId = OrderAnalyticsService.getAnalyticsOrderId(
              resp?.order?.orderHeader,
            );

            const trackingData = {
              analytics: {
                data: {
                  products,
                  order: {
                    numberOfShipments: order?.shipments?.length,
                    id: analyticsOrderId,
                    submitType:
                      resp?.order?.orderHeader?.orderStatus ===
                      OrderStatus.EDITING_SUBMITTED
                        ? OrderSubmittedAnalyticsMessages.editOrderEvent
                        : OrderSubmittedAnalyticsMessages.newOrderEvent,
                  },
                },
              },
              tracing: {
                data: {
                  isEndOfTrace: false,
                  isStartOfTrace: false,
                  traceContext: 'order',
                  attributes: {
                    event:
                      resp?.order?.orderHeader?.orderStatus ===
                      OrderStatus.EDITING_SUBMITTED
                        ? OrderTracingMessagesTest.editOrderEvent
                        : OrderTracingMessagesTest.submitOrderEvent,
                    order: {
                      orderId: filteredOrderId,
                      totalAmount: resp.order?.orderHeader.totalPrice,
                      openMethod: OrderSubmittedTracingMessages.openMethod,
                      tandemOrderId: resp.order?.orderHeader.tandemOrderNumber,
                      orderStatus: resp.order.orderHeader?.orderStatus,
                    },
                    errorDetails:
                      resp?.order?.orderHeader?.errorDetails || undefined,
                    products: tracingProducts,
                  },
                },
              },
            } as Tracking;
            if (
              !!resp &&
              resp.order?.orderHeader.orderStatus ===
                OrderStatus.EDITING_SUBMITTED
            ) {
              this.orderSubmittedAnalyticsService.trackEditSubmittedOrderApiSuccess(
                trackingData,
              );
            } else {
              this.orderAnalyticsService.trackSubmitOrderApiSuccess(
                trackingData,
              );
            }
            if (!resp.order?.orderHeader?.tandemOrderNumber) {
              this.orderTracingService.trackMonetateSubmitOrder(resp.order);
            }
          },
        ),
      ),
    { dispatch: false },
  );

  trackSubmitOrderApiFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CART_ACTIONS.submitCartFail),
        tap(error => {
          this.orderTracingService.trackSubmitOrderApiFail(error);
        }),
      ),
    { dispatch: false },
  );

  copyOrderSuccessTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.createCopiedOrderSuccess),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.orderService.getProductsForTracing$(
            payload?.newOrder?.orderItems,
          ),
        ]).pipe(take(1));
      }),
      map(([payload, productMap]) => {
        const products = SharedTrackingService.analyticsProductData(
          payload?.newOrder?.orderItems,
          productMap,
          payload?.newOrder?.orderHeader.divisionNumber,
        );
        const tracking: Tracking = {
          analytics: {
            data: {
              event: OrderAnalyticsMessages.copyOrder,
              capability: OrderAnalyticsMessages.capability,
              order: {
                id: payload?.newOrder?.orderHeader?.orderId,
                copiedId: payload?.previousOrder?.orderHeader?.orderId,
              },
              products,
            },
            transformFunc: genericAnalyticsTransformer,
          },
        };
        return CART_ANALYTICS_ACTIONS.trackCopyOrderSuccess({ tracking });
      }),
    ),
  );

  cancelSubmittedCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.cancelSubmittedCart),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.orderService.getProductsForTracing$(payload?.order?.orderItems),
        ]).pipe(take(1));
      }),
      map(([payload, productMap]) => {
        const products = SharedTrackingService.analyticsProductData(
          payload?.order?.orderItems,
          productMap,
          payload?.order?.orderHeader.divisionNumber,
        );
        const tracking = cancelSubmittedCartAnalytics(
          payload?.order?.orderHeader?.orderId,
          payload?.order?.orderHeader?.origOrderId,
          payload?.order?.orderHeader?.orderStatus,
          products,
        );
        return CART_ANALYTICS_ACTIONS.trackCancelSubmittedOrder({
          tracking,
        });
      }),
    ),
  );

  cancelIPCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.deleteCart),
      filter(
        payload =>
          payload.order.orderHeader.orderStatus === OrderStatus.IN_PROGRESS,
      ),
      withLatestFrom(this.store.select(productSelector)),
      map(([payload, productMap]) => {
        const products = SharedTrackingService.analyticsProductData(
          payload?.order?.orderItems,
          productMap,
          payload?.order?.orderHeader.divisionNumber,
        );
        const tracking = cancelInProgressCartAnalytics(
          payload?.order?.orderHeader?.orderId,
          payload?.order?.orderHeader?.origOrderId,
          products,
        );
        return CART_ANALYTICS_ACTIONS.trackCancelIPOrder({
          tracking,
        });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private panAppState: PanAppState,
    private orderSubmittedAnalyticsService: OrderSubmittedAnalyticsService,
    private orderAnalyticsService: OrderAnalyticsService,
    private orderTracingService: OrderTracingService,
    private orderService: OrderService,
  ) {}
}
